/**
 * 1 Nov 2026 (D-1102-OFFICE-1) — "Might slip — check these", on Today for the administrator and a co-administrator.
 * A short list of what is close to going wrong, each line with one action that opens the screen where it is fixed.
 * Five kinds (shared/might-slip.ts). It shows; nothing here sends, books or changes anything. Five lines are drawn and
 * the rest wait behind "Show all N" (the heading always says the whole count). Off in Admin → Settings: nothing is drawn.
 */
import { useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AGREEMENT_TITLE } from '@shared/agreement-cards';
import { MIGHT_SLIP_KINDS, mightSlipRole, type MightSlipLine, type MightSlipResponse } from '@shared/might-slip';
import type { Role } from '@shared/roles';
import { useOpenIn, type Program } from '@/app/program';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { formatDayShort } from '@/lib/dates';
import '@/styles/today1025.css';

/** Lines drawn before "Show all". */
export const SLIP_SHOWN = 5;

export function useMightSlip(role: Role | null | undefined) {
  return useQuery({
    queryKey: ['might-slip'],
    queryFn: () => apiFetch<MightSlipResponse>('/api/might-slip'),
    enabled: mightSlipRole(role),
    staleTime: 60_000,
    retry: false,
  });
}

const PROGRAM_OF: Partial<Record<MightSlipLine['kind'], Program>> = { unconfirmedVisit: 'housekeeping', noVolunteer: 'companion' };

function OpenLink({ href, program, children, ...rest }: { href: string; program: Program | null; children: ReactNode; className?: string; 'data-testid'?: string; 'aria-label'?: string }) {
  const openIn = useOpenIn();
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    openIn(href, program);
  };
  return (
    <Link to={href} onClick={onClick} {...rest}>
      {children}
    </Link>
  );
}

/** The line in words, in the person's language: who or what, and why it might slip. Counts and names only. */
export function slipWords(l: MightSlipLine, t: (k: string, o?: Record<string, unknown>) => string, lang: string): string {
  const day = l.dayKey ? formatDayShort(l.dayKey, lang) : '';
  const who = l.who ?? '';
  switch (l.kind) {
    case 'unconfirmedVisit':
      return t('slip1102.words.unconfirmedVisit', { who, day });
    case 'noVolunteer':
      return t('slip1102.words.noVolunteer', { who, day, activity: t(`slip1102.activity.${l.activity ?? 'other'}`) });
    case 'eventBelowTarget':
      return t('slip1102.words.eventBelowTarget', { name: l.label ?? '', day, have: l.have ?? 0, need: l.need ?? 0 });
    case 'checkIn':
      return t('slip1102.words.checkIn', { who, day });
    case 'agreementWaiting': {
      const title = l.agreement ? AGREEMENT_TITLE[l.agreement][lang === 'es' ? 'es' : 'en'] : '';
      return t(`slip1102.words.agreement.${l.standing ?? 'opened'}`, { who, title, count: l.days ?? 0 });
    }
  }
}

export function MightSlip({ role }: { role: Role | null | undefined }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const q = useMightSlip(role);
  const [all, setAll] = useState(false);
  const data = q.data;
  // the office only, and only while the switch is on; a failed read draws nothing rather than disturb Today
  if (!mightSlipRole(role) || !data || !data.enabled) return null;
  const lines = data.lines;
  const shown = all ? lines : lines.slice(0, SLIP_SHOWN);
  const hidden = lines.length - shown.length;
  const unavailable = (data.unavailable ?? []).filter((k) => (MIGHT_SLIP_KINDS as readonly string[]).includes(k)).map((k) => t(`slip1102.kind.${k}`));
  return (
    <section aria-labelledby="h-slip1102" className="t184-section slip1102" data-testid="slip1102">
      <h2 id="h-slip1102">{lines.length ? t('slip1102.titleCount', { count: lines.length }) : t('slip1102.title')}</h2>
      {lines.length ? (
        <>
          <ul className="slip1102-lines" aria-labelledby="h-slip1102">
            {shown.map((l) => {
              const words = slipWords(l, t, lang);
              const action = t(`slip1102.action.${l.kind}`);
              return (
                <li key={l.id} className="slip1102-line" data-kind={l.kind} data-testid={`slip1102-line-${l.id}`}>
                  <span className="slip1102-what">{words}</span>
                  <OpenLink href={l.href} program={PROGRAM_OF[l.kind] ?? null} className="btn secondary sm slip1102-go" aria-label={`${action} — ${words}`} data-testid={`slip1102-open-${l.id}`}>
                    {action}
                  </OpenLink>
                </li>
              );
            })}
          </ul>
          {lines.length > SLIP_SHOWN ? (
            <Button size="sm" variant="secondary" aria-expanded={all} onClick={() => setAll((v) => !v)} data-testid="slip1102-toggle">
              {all ? t('slip1102.fewer') : t('slip1102.all', { count: hidden || lines.length })}
            </Button>
          ) : null}
        </>
      ) : (
        <p className="help" data-testid="slip1102-none">
          {t('slip1102.none')}
        </p>
      )}
      {unavailable.length ? (
        <p className="help" role="status" data-testid="slip1102-unavailable">
          {t('slip1102.unavailable', { list: unavailable.join(', ') })}
        </p>
      ) : null}
    </section>
  );
}
